import React, { ReactElement, useState } from "react"
import { Modal, Typography, message } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation, faTrash } from "@fortawesome/free-solid-svg-icons"
import KosmoButton from "../../Buttons/KosmoButton"
import { useTranslation } from "react-i18next"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { groupBy } from "lodash"
import { Stop } from "@/types/stops.types"
import { putCancelMultipleStops } from "@/api/stops"
import { useSetRecoilState } from "recoil"
import { stopsAtom } from "@/atoms/stopsAtom"
import { DestinationStatuses } from "@/types/orders.types"

const { Title } = Typography

interface CancelStopsModalProps {
  stops: Stop[]
  button?: ReactElement
  disabled?: boolean
  callback?: () => Promise<void>
}

const CancelStopsModal: React.FC<CancelStopsModalProps> = ({
  stops,
  button,
  disabled,
  callback,
}) => {
  const { t } = useTranslation()
  const setStops = useSetRecoilState(stopsAtom)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const groupedDeliveries = groupBy(stops, "id")
  const payload = Object.keys(groupedDeliveries)
    .map((orderId) => ({
      deliveryId: orderId,
      stopIndexes: groupedDeliveries[orderId]?.map((stop) => stop?.stopIndex) || [],
    }))
    ?.filter((delivery) => !!delivery.deliveryId && delivery?.stopIndexes?.length > 0)

  const showModal = () => {
    setIsModalVisible(true)
  }
  const handleOk = () => {
    cancelMultipleStops()
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const cancelMultipleStops = async () => {
    setIsLoading(true)
    try {
      await putCancelMultipleStops(payload)
      setStops((stopsState) => {
        const updatedStops = stopsState?.stops.map((stop) => {
          const updatedDelivery = payload?.find(
            (delivery) =>
              delivery?.deliveryId === stop?.id && delivery?.stopIndexes?.includes(stop?.stopIndex)
          )
          if (!updatedDelivery) return stop
          return {
            ...stop,
            recipient: { ...stop?.recipient, status: DestinationStatuses.CANCELED },
          }
        })
        return { ...stopsState, stops: updatedStops }
      })

      if (callback) {
        await callback()
      }
      setIsModalVisible(false)
    } catch (error) {
      console.log(error)
      message.error(t("errors.common"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {!!button ? (
        React.cloneElement(button, {
          onClick: (e: any) => {
            showModal()
            e.preventDefault()
          },
        })
      ) : (
        <KosmoButton
          onClick={showModal}
          size="middle"
          type="default"
          danger
          classname=""
          isDisabled={disabled}
        >
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faTrash} className="text-gray-600" />
            <p>
              {stops.length > 1
                ? t("active-orders.CancelStopsModal.cancelStops")
                : t("active-orders.CancelStopsModal.cancelStop")}
            </p>
          </div>
        </KosmoButton>
      )}
      <Modal
        className="cancelRideModal"
        centered={true}
        open={isModalVisible}
        footer={null}
        style={{ width: "75%" }}
        closable={true}
        onCancel={handleCancel}
        destroyOnClose
        styles={{ body: { alignItems: "center", display: "flex", justifyContent: "center" } }}
      >
        <div className="flex flex-col items-center w-full gap-y-6 text-center">
          <div className="flex flex-col gap-y-3">
            <FontAwesomeIcon
              className="flex-shrink-0 text-red-500 text-2xl"
              icon={faCircleExclamation as IconProp}
            />
            <Title level={4}>
              {stops.length > 1
                ? t("active-orders.CancelStopsModal.cancelStops", { count: stops.length })
                : t("active-orders.CancelStopsModal.cancelStop")}
            </Title>
          </div>
          <p className="text-center">
            {t("active-orders.CancelOrdersModal.cancelStopConfirmation", {
              count: stops.length,
            })}
          </p>
          <div className="flex flex-col w-9/12 gap-y-3 items-center">
            <KosmoButton
              type="success"
              onClick={handleCancel}
              classname="w-full"
              isLoading={isLoading}
              isDisabled={isLoading}
            >
              {t("common.noAbort")}
            </KosmoButton>
            <KosmoButton
              danger
              onClick={handleOk}
              classname="w-full"
              isLoading={isLoading}
              isDisabled={isLoading}
            >
              {t("active-orders.CancelOrdersModal.confirmCancelButton")}
            </KosmoButton>
          </div>
        </div>
      </Modal>
    </>
  )
}
export default CancelStopsModal
